import { useId } from 'react'
import { Check, Info, X } from 'lucide-react'
import { OptionsCombobox } from '@/components/builder/OptionsCombobox'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import type { FetchPickerOptions, PickerOption } from '@/lib/projectOptions'

export interface TagRowPicker {
  source: string
  group: string | null
  awaitedParent: string | null
  fetchOptions: FetchPickerOptions
  onPick: (option: PickerOption) => void
}

interface TagRowProps {
  label: string
  queryKey: string
  description?: string
  value: string
  onChange: (value: string) => void
  onRemove: () => void
  picker?: TagRowPicker
  pickedValue?: string
  removeLabel?: string
  autoFocus?: boolean
  collidesWithSibling?: boolean
}

export function TagRow({
  label,
  queryKey,
  description,
  value,
  onChange,
  onRemove,
  picker,
  pickedValue,
  removeLabel,
  autoFocus,
  collidesWithSibling,
}: TagRowProps) {
  const id = useId()
  const descriptionId = useId()
  const awaitingId = useId()
  const errorId = useId()
  const awaitedParent = picker?.awaitedParent ?? null
  const error = collidesWithSibling === true ? 'Duplicate parameters are not allowed' : null
  // The actionable half goes first: a description can be long.
  const describedBy =
    [
      error !== null ? errorId : null,
      awaitedParent !== null ? awaitingId : null,
      description ? descriptionId : null,
    ]
      .filter((x) => x !== null)
      .join(' ') || undefined
  return (
    <div className="space-y-1">
      <div className="flex items-baseline justify-between gap-2">
        <div className="flex items-center gap-1">
          <Label htmlFor={id}>{label}</Label>
          {description ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  tabIndex={-1}
                  aria-hidden
                  className="text-muted-foreground hover:text-foreground"
                >
                  <Info className="size-3.5" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs">
                {description}
              </TooltipContent>
            </Tooltip>
          ) : null}
        </div>
        <span className="font-mono text-xs text-muted-foreground">{queryKey}</span>
      </div>
      <div className="flex gap-2">
        {picker ? (
          <OptionsCombobox
            id={id}
            value={value}
            source={picker.source}
            group={picker.group}
            awaitingParent={awaitedParent !== null}
            fetchOptions={picker.fetchOptions}
            onChangeText={onChange}
            onPick={picker.onPick}
            describedBy={describedBy}
            autoFocus={autoFocus}
            invalid={error !== null}
          />
        ) : (
          <Input
            id={id}
            spellCheck={false}
            autoComplete="off"
            placeholder="Value"
            value={value}
            autoFocus={autoFocus}
            onChange={(e) => onChange(e.target.value)}
            aria-invalid={error !== null || undefined}
            aria-describedby={describedBy}
          />
        )}
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={removeLabel ?? `Remove ${label}`}
          onClick={onRemove}
        >
          <X className="size-4" aria-hidden />
        </Button>
      </div>
      <div className="min-h-4">
        {error !== null && (
          <p id={errorId} className="text-xs text-destructive">
            {error}
          </p>
        )}
        {awaitedParent !== null && (
          <p id={awaitingId} className="text-xs text-muted-foreground">
            Options appear once {awaitedParent} is picked.
          </p>
        )}
        {pickedValue !== undefined && (
          <p className="flex items-center gap-1 text-xs text-muted-foreground">
            <Check className="size-3" aria-hidden />
            <span>
              ID <span className="font-mono">{pickedValue}</span>
            </span>
          </p>
        )}
      </div>
      {description ? (
        <span id={descriptionId} className="sr-only">
          {description}
        </span>
      ) : null}
    </div>
  )
}
